import type { VitesseResultToast, ResultToastItem } from '@vitesse/base-ui';
import '@vitesse/base-ui'; // side-effect: registers <vitesse-result-toast>

/*
 * showResultError — the app's ONE seam onto the shared Result-failure surface
 * (<vitesse-result-toast>, plans-entitlements.md §12.3). Success renders
 * nothing anywhere; failures land here so refusal UX cannot drift per pane.
 *
 * The server formats a D19 denial as the COMPLETE action list in the error
 * message ("requires the X permission — ask your organization admin…; and your organization's
 * plan does not include Y — upgrade…"), so the toast is fully informative from
 * text alone. The STRUCTURED suite (vitessev1.Error + `also`, as a Connect
 * error detail) does NOT survive the Comlink hop from the Backend Worker —
 * structured-clone drops custom Error fields — so per-code ACTION CHIPS (route
 * to billing / fleet UI) land together with the worker-side decode when the
 * first real chip destination exists. Splitting on the server's "; and "
 * separator below is DISPLAY-ONLY line-breaking, never routing — routing by
 * string is exactly what the structured suite exists to prevent.
 */

let host: VitesseResultToast | null = null;

/** The lazily self-mounted toast host — body-appended so no page owns it and
 *  no template needs touching to adopt it. */
function toastHost(): VitesseResultToast {
  if (host && host.isConnected) return host;
  host = document.querySelector('vitesse-result-toast');
  if (!host) {
    host = document.createElement('vitesse-result-toast');
    document.body.appendChild(host);
  }
  return host;
}

/** Show a failed Result/Connect error on the shared toast. `heading` names the
 *  action that failed ("Write refused"); the body is the server's own denial
 *  suite, one entry per refusal. */
export function showResultError(err: unknown, heading?: string): void {
  const raw = err instanceof Error ? err.message : String(err);
  // Connect prefixes messages with "[permission_denied] " — strip for display.
  const msg = raw.replace(/^\[[a-z_ ]+\]\s*/i, '');
  const items: ResultToastItem[] = msg
    .split('; and ')
    .filter((s) => s.trim().length > 0)
    .map((text) => ({ code: '', explanation: text.trim() }));
  if (items.length === 0) items.push({ code: '', explanation: 'The action failed.' });
  toastHost().push({ heading, items });
}
